import {
  Add,
  ArrowRightOutlined,
  CameraAltOutlined,
  DeleteOutlineOutlined,
  Edit,
  GridView,
  ImageOutlined,
  Info,
  Search,
} from "@mui/icons-material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import {
  Autocomplete,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import PenForEdit from "../../assets/svg/PenForEdit.svg";
import defoult from "../../assets/svg/defultImage.svg";
import trashIcon from "../../assets/svg/delete.png";
import DeleteModal from "../../components/Modals/DeleteModal";
import "./Modalss.css";

const API_BASE_URL = "https://estbn.com/EstbnNew/public";
const IMAGE_BASE_URL = "https://estbn.com/EstbnNew/public/";

const EQModals = () => {

  const [viewMode, setViewMode] = useState("cards");
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewBy, setViewBy] = useState("Models");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [equipmentItems, setEquipmentItems] = useState([]);
  const [manufacturersList, setManufacturersList] = useState([]);
  const [equipmentCategoriesList, setEquipmentCategoriesList] = useState([]);

  const [editingItem, setEditingItem] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });

  const [totalElements, setTotalElements] = useState(0);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;

  const isAbsoluteUrl = (url) =>
    url && (url.startsWith("http://") || url.startsWith("https://"));

  const fetchEquipmentModels = useCallback(async () => {
    if (!token) {
      toast.error("Authentication token not available.");
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/portal/equipment-models`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: paginationModel.page + 1,
            per_page: paginationModel.pageSize,
          },
        }
      );

      const mappedItems = response.data.data.equipment_models.map((item) => {
        let finalImageUrl = defoult;

        if (item.default_image_url) {
          if (isAbsoluteUrl(item.default_image_url)) {
            finalImageUrl = item.default_image_url;
          } else {
            const cleanImageUrlPath = item.default_image_url.startsWith("/")
              ? item.default_image_url.substring(1)
              : item.default_image_url;
            finalImageUrl = `${IMAGE_BASE_URL}${cleanImageUrlPath}`;
          }
        }

        return {
          id: item.id,
          reference: item.reference,
          imageUrl: finalImageUrl,
          name: item.name,
          description: item.description,
          category: item.category?.parent?.name || item.category?.name || "",
          subCategory: item.category?.parent ? item.category?.name : "",
          itemsNumber: item.active_equipment_count,
          manufacturer: item.manufacturer?.name || "",
          model: item.name,
          manufactureDate: item.created_at
            ? new Date(item.created_at).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "",
          category_id: item.category?.id || "",
          manufacturer_id: item.manufacturer?.id || "",
        };
      });
      setEquipmentItems(mappedItems);
      setTotalElements(response.data.data.pagination.total);
    } catch (err) {
      console.error("Error fetching equipment models:", err);
      toast.error("Failed to fetch equipment models.");
    }
  }, [token, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchEquipmentModels();
  }, [fetchEquipmentModels]);

  const fetchManufacturers = useCallback(async () => {
    if (!token) {
      toast.error("Authentication token not available for manufacturers.");
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/portal/manufacturers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setManufacturersList(response.data.data.manufacturers);
    } catch (err) {
      console.error("Error fetching manufacturers:", err);
      toast.error("Failed to fetch manufacturers.");
    }
  }, [token]);

  useEffect(() => {
    fetchManufacturers();
  }, [fetchManufacturers]);

  const fetchEquipmentCategories = useCallback(async () => {
    if (!token) {
      toast.error(
        "Authentication token not available for equipment categories."
      );
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/portal/equipment-categories?type=main`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const categoriesData = response.data.data.categories;
      let flattenedCategories = [];

      categoriesData.forEach((mainCategory) => {
        flattenedCategories.push({
          id: mainCategory.id,
          name: mainCategory.name,
        });
        if (mainCategory.children && mainCategory.children.length > 0) {
          mainCategory.children.forEach((subCategory) => {
            flattenedCategories.push({
              id: subCategory.id,
              name: `${mainCategory.name} > ${subCategory.name}`,
            });
          });
        }
      });

      setEquipmentCategoriesList(flattenedCategories);
    } catch (err) {
      console.error("Error fetching equipment categories:", err);
      toast.error("Failed to fetch equipment categories.");
    }
  }, [token]);

  useEffect(() => {
    fetchEquipmentCategories();
  }, [fetchEquipmentCategories]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      modelName: "",
      service: "",
      manufacture: "",
      description: "",
    },
  });

  const handleClose = () => {
    setShow(false);
    setUploadedImage(null);
    setSelectedFile(null);
    setEditingItem(null);
    reset();
  };

  const handleShow = (item = null) => {
    setEditingItem(item);
    setShow(true);
    if (item) {
      reset({
        modelName: item.name,
        service: item.category_id,
        manufacture: item.manufacturer_id,
        description: item.description,
      });
      setUploadedImage(item.imageUrl);
      setSelectedFile(null);
    } else {
      reset({
        modelName: "",
        service: "",
        manufacture: "",
        description: "",
      });
      setUploadedImage(null);
      setSelectedFile(null);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setUploadedImage(editingItem ? editingItem.imageUrl : null);
    }
  };

  const handleDeleteDefaultImage = async (e) => {
    e.stopPropagation();
    if (!editingItem || !token) {
      toast.error("Cannot delete image: no item selected or token missing.");
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/portal/equipment-models/${editingItem.id}/image`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Default image deleted successfully!");
      setUploadedImage(defoult);
      setSelectedFile(null);
      fetchEquipmentModels();
    } catch (error) {
      console.error(
        "Error deleting default image:",
        error.response ? error.response.data : error.message
      );
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to delete image: ${errorMessage}`);
    }
  };

  const onSubmit = async (data) => {
    if (!token) {
      toast.error("Authentication token is missing. Please log in.");
      return;
    }
    try {
      if (editingItem) {
        const modelFormData = new FormData();
        modelFormData.append("name", data.modelName);
        if (data.service) {
          modelFormData.append("category_id", data.service);
        }
        modelFormData.append("manufacturer_id", data.manufacture);
        modelFormData.append("description", data.description);
        modelFormData.append("_method", "PATCH");

        await axios.post(
          `${API_BASE_URL}/api/portal/equipment-models/${editingItem.id}`,
          modelFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success("Model details updated successfully!");

        if (selectedFile) {
          const imageFormData = new FormData();
          imageFormData.append("default_image", selectedFile);

          await axios.post(
            `${API_BASE_URL}/api/portal/equipment-models/${editingItem.id}/image`,
            imageFormData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );
          toast.success("Model image updated successfully!");
        }
      } else {
        const formData = new FormData();
        formData.append("name", data.modelName);
        if (data.service) {
          formData.append("category_id", data.service);
        }
        formData.append("manufacturer_id", data.manufacture);
        formData.append("description", data.description);
        if (selectedFile) {
          formData.append("default_image", selectedFile);
        }

        await axios.post(
          `${API_BASE_URL}/api/portal/equipment-models`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.success("New model created successfully!");
      }

      handleClose();
      fetchEquipmentModels();
    } catch (error) {
      console.error(
        "Error saving model:",
        error.response ? error.response.data : error.message
      );
      const errors = error.response?.data?.errors;
      let errorMessage = error.response?.data?.message || error.message;

      if (errors) {
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey) {
          errorMessage = errors[firstErrorKey][0];
        }
      }
      toast.error(`Failed to save model: ${errorMessage}.`);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEditItem = (item) => {
    handleShow(item);
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const confirmDelete = async () => {
    if (!itemToDelete || !token) {
      toast.error("Missing data for deletion.");
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/portal/equipment-models/${itemToDelete.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Equipment model deleted successfully!");
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchEquipmentModels();
    } catch (error) {
      console.error(
        "Error deleting equipment model:",
        error.response ? error.response.data : error.message
      );
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to delete equipment model: ${errorMessage}`);
    }
  };

  const handleCardClick = (item) => {
    console.log("Navigating to details for equipment item:", item.id);
  };

  const handleViewByChange = (event) => {
    const selectedOption = event.target.value;
    setViewBy(selectedOption);

    if (selectedOption === "Main Categories") {
      navigate("/equipments");
    } else if (selectedOption === "Sub Categories") {
      navigate(`/subcategory`);
    } else if (selectedOption === "All Equipment") {
      navigate(`/AllEquipments`);
    }
  };

  const columns = [
    {
      field: "reference",
      headerName: "Reference",
      flex: 1.4,
      minWidth: 200,
      renderCell: (params) => {
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {params.value || "-"}
        </div>;
      }
    },
    {
      field: "name",
      headerName: "Equipment Name",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <div className="d-flex align-items-center">
          <img
            src={params.row.imageUrl}
            alt={params.value}
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              marginRight: 8,
              objectFit: "cover",
            }}
          />
          {params.value}
        </div>
      ),
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "subCategory",
      headerName: "Sub Category",
      flex: 1.2,
      minWidth: 130,
    },
    {
      field: "itemsNumber",
      headerName: "Items Number",
      flex: 0.8,
      minWidth: 90,
    },
    {
      field: "manufacturer",
      headerName: "Manufacturer",
      flex: 1,
      minWidth: 110,
    },
    {
      field: "model",
      headerName: "Model Name",
      flex: 1,
      minWidth: 110,
    },
    {
      field: "Actions",
      headerName: "Action",
      flex: 1.2,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="d-flex justify-content-evenly align-items-center h-100 w-100">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleEditItem(params.row);
              }}
              sx={{ color: "" }}
              aria-label="edit"
              size="small"
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteItem(params.row);
              }}
              sx={{ color: "error.main" }}
              aria-label="delete"
              size="small"
            >
              <img
                src={trashIcon}
                alt="Delete"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick(params.row);
              }}
              aria-label="navigate"
              size="small"
              color="default"
            >
              <ArrowRightOutlined />
            </IconButton>
          </div>
        );
      },
    },
  ];

  const filteredEquipmentItems = equipmentItems.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.reference?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(totalElements / paginationModel.pageSize);
  const currentPageIndex = paginationModel.page;

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="custom-modal-size"
      >
        <Modal.Header style={{ backgroundColor: "#D5D5D5" }}>
          <Modal.Title className="text-black">
            {editingItem ? "Edit Model" : "Add New Model"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex flex-column align-items-center mb-4">
              <div
                className="position-relative"
                style={{ width: "100px", height: "100px" }}
              >
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    border: "2px dashed #ccc",
                    overflow: "hidden",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  {uploadedImage ? (
                    <img
                      src={uploadedImage}
                      alt="Uploaded"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <ImageOutlined sx={{ fontSize: 40, color: "#ccc" }} />
                  )}
                </div>
                <label
                  htmlFor="image-upload"
                  className="position-absolute bottom-0 end-0 p-1 bg-dark rounded-circle d-flex justify-content-center align-items-center cursor-pointer"
                  style={{ transform: "translate(25%, 25%)" }}
                >
                  <CameraAltOutlined sx={{ fontSize: 20, color: "#fff" }} />
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </label>
                {editingItem &&
                  uploadedImage !== defoult &&
                  uploadedImage !== null &&
                  !selectedFile && (
                    <IconButton
                      onClick={handleDeleteDefaultImage}
                      className="position-absolute bottom-0 start-0 p-1 bg-danger rounded-circle d-flex justify-content-center align-items-center"
                      style={{ transform: "translate(-25%, 25%)", zIndex: 1 }}
                      size="small"
                      aria-label="delete image"
                    >
                      <DeleteOutlineOutlined
                        sx={{ fontSize: 20, color: "#fff" }}
                      />
                    </IconButton>
                  )}
              </div>
            </div>

            <div className="my-2">
              <label htmlFor="modelNameInput">
                Model Name <span className="text-danger">*</span>
              </label>
              <TextField
                id="modelNameInput"
                size="small"
                className="w-100"
                type="text"
                placeholder="Enter Name"
                {...register("modelName", {
                  required: "Model Name is required",
                })}
                error={!!errors.modelName}
                helperText={errors.modelName?.message}
              />
            </div>

            {/* REPLACED CATEGORY SELECT WITH AUTOCOMPLETE */}
            <div className="my-2">
              <label htmlFor="categorySelect">
                Category <span className="text-danger">*</span>
              </label>
              <Controller
                name="service"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    id="category-autocomplete"
                    options={equipmentCategoriesList}
                    getOptionLabel={(option) => option.name || ""}
                    // Handle the value to match the ID stored in form state
                    value={
                      equipmentCategoriesList.find(
                        (c) => c.id === field.value,
                      ) || null
                    }
                    // Update form state with ID
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.id : "");
                    }}
                    renderOption={(props, option) => (
                      <li {...props} className="p-2">
                        <div className="label-size m-0">{option.name}</div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select Category"
                        className="w-100"
                        error={!!errors.service}
                        helperText={errors.service?.message}
                      />
                    )}
                  />
                )}
              />
            </div>

            {/* REPLACED MANUFACTURE SELECT WITH AUTOCOMPLETE */}
            <div className="my-2">
              <label htmlFor="manufactureSelect">
                Manufacturer <span className="text-danger">*</span>
              </label>
              <Controller
                name="manufacture"
                control={control}
                rules={{ required: "Manufacturer is required" }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    id="manufacture-autocomplete"
                    options={manufacturersList}
                    getOptionLabel={(option) => option.name || ""}
                    // Handle the value to match the ID stored in form state
                    value={
                      manufacturersList.find((m) => m.id === field.value) ||
                      null
                    }
                    // Update form state with ID
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.id : "");
                    }}
                    renderOption={(props, option) => (
                      <li {...props} className="p-2">
                        <div className="label-size m-0">{option.name}</div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select Manufacturer"
                        className="w-100"
                        error={!!errors.manufacture}
                        helperText={errors.manufacture?.message}
                      />
                    )}
                  />
                )}
              />
            </div>

            <div className="my-3">
              <label htmlFor="descriptionInput">Model Description</label>
              <textarea
                id="descriptionInput"
                style={{
                  padding: "10px",
                  borderRadius: "15px",
                  border: "solid 2px #D8D8D8",
                }}
                rows={3}
                className="w-100"
                placeholder="Add Description"
                {...register("description")}
              />
            </div>

            <div className="d-flex gap-3 justify-content-center align-items-center custom-card-sm my-3">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-light px-5"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-dark px-5">
                {editingItem ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      <DeleteModal
        showDelete={showDeleteModal}
        handleDeleteClose={handleDeleteClose}
        textMessage="Are you sure you want to delete this equipment model? This action cannot be undone."
        deleteFunction={confirmDelete}
        itemName={itemToDelete?.name || ""}
      />
      <div className="m-4">
        <div className="d-flex justify-content-between align-items-center w-100">
          <h6 className="m-0">Equipment</h6>
          <button onClick={() => handleShow()} className="btn btn-light mx-1">
            <Add /> New Model
          </button>
        </div>
        <div className="d-flex flex-wrap justify-content-between align-items-center w-100 my-3">
          <div
            className="input-group flex-nowrap justify-content-end align-items-center rounded-2"
            style={{ width: "400px", maxWidth: "100%" }}
          >
            <input
              type="text"
              className="form-control bg-transparent position-relative rounded"
              placeholder="Search for equipment .."
              value={searchTerm}
              onChange={handleSearch}
              style={{ paddingRight: "40px" }}
            />
            <div className="position-absolute mx-2">
              <Search color="action" />
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-end gap-2 w-50">
            <span>View by</span>
            <Select
              className="w-25"
              size="small"
              value={viewBy}
              onChange={handleViewByChange}
            >
              {[
                "Main Categories",
                "Sub Categories",
                "Models",
                "All Equipment",
              ].map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  className="custom-menu-item"
                >
                  {option}
                </MenuItem>
              ))}
            </Select>

            <div className="">
              <button
                onClick={() => setViewMode("cards")}
                className={`btn ${
                  viewMode === "cards" ? "btn-dark" : "btn-light"
                }`}
              >
                <GridView />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`btn ${
                  viewMode === "table" ? "btn-dark" : "btn-light"
                }`}
              >
                <FormatListBulletedIcon />
              </button>
            </div>
          </div>
        </div>
        {viewMode === "cards" && (
          <>
            <div className="models-cards-grid m-0">
              {filteredEquipmentItems.map((item) => (
                <div
                  key={item.id}
                  className="model-card cursorPointer"
                  onClick={() => handleCardClick(item)}
                >
                  <div className="model-icons-top">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditItem(item);
                      }}
                      className="icon-button-style"
                    >
                      <img src={PenForEdit} alt="Edit" className="icon-img" />
                    </IconButton>

                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteItem(item);
                      }}
                      className="icon-button-style"
                    >
                      <img src={trashIcon} alt="Delete" className="icon-img" />
                    </IconButton>
                  </div>

                  <div className="model-image-wrapper">
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="model-image"
                    />
                  </div>

                  <div className="model-title-container">
                    <span className="model-title">{item.name}</span>
                    <Tooltip title={item.description} placement="top">
                      <Info sx={{ width: 20 }} className="info-icon" />
                    </Tooltip>
                  </div>

                  <div className="model-details">
                    <div className="model-detail-row">
                      <span className="detail-label">Equipment Ref.</span>
                      <span className="detail-value">{item.reference || "-"}</span>
                    </div>
                    <div className="model-detail-row">
                      <span className="detail-label">Manufacture</span>
                      <span className="detail-value">{item.manufacturer}</span>
                    </div>
                    <div className="model-detail-row">
                      <span className="detail-label">Category</span>
                      <span className="detail-value">{item.category}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center mt-3 gap-2">
              <Button
                variant="outlined"
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: Math.max(0, prev.page - 1),
                  }))
                }
                disabled={currentPageIndex === 0}
              >
                Previous
              </Button>
              <Typography variant="body1" className="align-self-center">
                Page {currentPageIndex + 1} of{" "}
                {totalPages === 0 ? 1 : totalPages}
              </Typography>
              <Button
                variant="outlined"
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: Math.min(totalPages - 1, prev.page + 1),
                  }))
                }
                disabled={
                  currentPageIndex >= totalPages - 1 || totalPages === 0
                }
              >
                Next
              </Button>
            </div>
          </>
        )}
        {viewMode === "table" && (
          <div className="data-grid-small">
            <Paper sx={{ height: "auto", width: "100%" }}>
              <DataGrid
                autoHeight
                rows={filteredEquipmentItems}
                columns={columns}
                rowCount={totalElements}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 15, 20, 50]}
                sx={{ border: 0 }}
                checkboxSelection
                disableRowSelectionOnClick
              />
            </Paper>
          </div>
        )}
      </div>
    </>
  );
};

export default EQModals;
